import { Avatar, Box, Link, List, ListItem, ListItemAvatar, ListItemText, ListSubheader, Paper, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { useAllExercisesQuery } from '@/hooks/query-hooks';
import { pathMap } from '@/navigation/paths';
import { ExerciseDTO, ExercisePrerequisiteDTO } from '@/generated';
import { exerciseDetailsListStyle, exerciseDetailsListSubheaderStyle, exerciseDetailsPaperListElevation, exerciseDetailsPaperListStyle } from '@/styles';
import LinkIcon from '@mui/icons-material/Link';
import { ItemPreviewTooltip } from '@/components/ItemPreviewTooltip';

export const ExerciseProRegressionList = (props: {
  ids: string[];
  title: string;
  sx?: SxProps<Theme>;
  descriptions?: Record<string, string | undefined>;
  showExerciseTooltip?: boolean;
}) => {
  const { ids, title, sx } = props;
  return (
    <Paper sx={{ ...exerciseDetailsPaperListStyle, ...sx }} elevation={exerciseDetailsPaperListElevation}>
      <List sx={exerciseDetailsListStyle} component={'nav'}>
        <ListSubheader sx={exerciseDetailsListSubheaderStyle}>{title}</ListSubheader>
        {ids.map((id) => (
          <ProgressionListItem key={id} id={id} description={props.descriptions?.[id]} showExerciseTooltip={props.showExerciseTooltip} />
        ))}
        {!ids.length && (
          <ListItem>
            <ListItemText>No {title}</ListItemText>
          </ListItem>
        )}
      </List>
    </Paper>
  );
};

export const ExercisePrerequisiteList = (props: { prerequisites: ExercisePrerequisiteDTO[]; sx?: SxProps<Theme> }) => {
  const { exercises } = useAllExercisesQuery(true);

  return (
    <Paper sx={{ ...exerciseDetailsPaperListStyle, ...props.sx }} elevation={exerciseDetailsPaperListElevation}>
      <List sx={exerciseDetailsListStyle} component={'nav'}>
        <ListSubheader sx={exerciseDetailsListSubheaderStyle}>Prerequisites</ListSubheader>
        {props.prerequisites.map((prerequisite) => {
          const exercise = exercises?.find((candidate) => candidate.id === prerequisite.exerciseId);
          const target = getLinkTarget(exercise);
          const requirements = formatPrerequisiteRequirements(prerequisite);

          const content = (
            <ListItem key={prerequisite.exerciseId}>
              <ListItemAvatar>
                <Avatar sx={{ width: 35, height: 35, m: 1, backgroundColor: 'white' }}>
                  <Link href={target}>
                    <ImageFromServer sx={{ maxWidth: 35, maxHeight: 35 }} thumbnail={true} alt="" id={exercise?.imageId} />
                  </Link>
                </Avatar>
              </ListItemAvatar>
              <Link sx={{ color: 'common.white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.75 }} href={target}>
                <LinkIcon fontSize="small" />
                <ListItemText primary={exercise?.name ?? prerequisite.exerciseId} secondary={requirements} />
              </Link>
            </ListItem>
          );

          return exercise ? (
            <ItemPreviewTooltip key={prerequisite.exerciseId} name={exercise.name} description={exercise.description} imageId={exercise.imageId}>
              {content}
            </ItemPreviewTooltip>
          ) : (
            content
          );
        })}
        {!props.prerequisites.length && (
          <ListItem>
            <ListItemText>No Prerequisites</ListItemText>
          </ListItem>
        )}
      </List>
    </Paper>
  );
};

export const SimpleExercisePrerequisiteList = (props: { prerequisites: ExercisePrerequisiteDTO[] }) => {
  const { exercises } = useAllExercisesQuery(true);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={500}>
        Prerequisites:
      </Typography>
      {props.prerequisites.map((prerequisite) => {
        const exercise = exercises?.find((candidate) => candidate.id === prerequisite.exerciseId);
        return (
          <Typography
            key={prerequisite.exerciseId}
            variant="caption"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}
          >
            <Link
              color="inherit"
              underline="none"
              href={getLinkTarget(exercise)}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {exercise?.name ?? prerequisite.exerciseId}
            </Link>
            : {formatPrerequisiteRequirements(prerequisite)}
          </Typography>
        );
      })}
      {!props.prerequisites.length && <Typography variant="caption">None</Typography>}
    </Box>
  );
};

const ProgressionListItem = (props: { id: string; description?: string; showExerciseTooltip?: boolean }) => {
  const { id } = props;
  const exercises = useAllExercisesQuery(true);
  const exercise = exercises.data?.find((e) => e.id === id);
  const wh = 35;
  const target = `${process.env.REACT_APP_PUBLIC_URL}${pathMap.ExerciseDetails}/${exercise?.id}`;

  const content = (
    <ListItem>
      <ListItemAvatar>
        <Avatar sx={{ width: wh, height: wh, m: 1, backgroundColor: 'white' }}>
          <Link href={target}>
            <ImageFromServer sx={{ maxWidth: wh, maxHeight: wh }} thumbnail={true} alt="" id={exercise?.imageId} />
          </Link>
        </Avatar>
      </ListItemAvatar>
      <Link sx={{ color: 'common.white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.75 }} href={target}>
        <LinkIcon fontSize="small" />
        <ListItemText>{`${exercise?.name}`}</ListItemText>
      </Link>
    </ListItem>
  );

  return exercise && props.showExerciseTooltip ? (
    <ItemPreviewTooltip name={exercise.name} description={exercise.description} imageId={exercise.imageId} additionalText={props.description}>
      {content}
    </ItemPreviewTooltip>
  ) : (
    content
  );
};

export const SimpleProgressionRegressionList = (props: { ids: string[]; title: string; descriptions?: Record<string, string | undefined> }) => {
  const { ids, title } = props;
  const { exercises } = useAllExercisesQuery(true);

  return (
    <Typography sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} variant="body2">
      {title}:&nbsp;
      {ids.map((id, i) => {
        const exercise = exercises?.find((ex) => ex.id === id);

        const link = (
          <Link
            color="inherit"
            underline="none"
            sx={{}}
            href={getLinkTarget(exercise)}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {exercise?.name}{' '}
          </Link>
        );

        return (
          <Box component="span" key={id}>
            {i > 0 && ', '}
            {props.descriptions?.[id] ? (
              <Tooltip title={props.descriptions[id]} arrow>
                {link}
              </Tooltip>
            ) : (
              link
            )}
          </Box>
        );
      })}
      {!ids.length ? 'None' : ''}
    </Typography>
    // {title}:
  );
};

//  {ids.map((id, i) => exercises?.find((ex) => ex.id === id)?.name).join(', ')}
// {!ids?.length ? 'None' : ''}

const getLinkTarget = (exercise?: ExerciseDTO) => `${process.env.REACT_APP_PUBLIC_URL}${pathMap.ExerciseDetails}/${exercise?.id}`;

const formatPrerequisiteRequirements = (prerequisite: ExercisePrerequisiteDTO) =>
  [
    prerequisite.minSets !== undefined ? `${prerequisite.minSets} sets` : undefined,
    prerequisite.minReps !== undefined ? `${prerequisite.minReps} reps` : undefined,
    prerequisite.minDurationSeconds !== undefined ? `${prerequisite.minDurationSeconds} sec` : undefined,
    prerequisite.minLoadKg !== undefined ? `${prerequisite.minLoadKg} kg` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
